import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import MultiSelectBusca from '../../components/MultiSelectBusca';
import api from '../../api';

export default function TrilhaEditor() {
  const { id } = useParams();
  const editando = !!id;
  const navigate = useNavigate();

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [obrigatoria, setObrigatoria] = useState(false);
  const [sequencial, setSequencial] = useState(true);
  const [publicada, setPublicada] = useState(true);
  const [perfisSel, setPerfisSel] = useState([]);
  const [itens, setItens] = useState([]); // {tipo, documento_id|quiz_id, titulo}

  const [perfis, setPerfis] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [salvando, setSalvando] = useState(false);

  // Recarrega as listas ao voltar pra aba: aula ou quiz criado em outra aba já aparece aqui.
  useEffect(() => {
    const carregar = () => Promise.all([api.get('/perfis'), api.get('/documentos'), api.get('/quizzes')])
      .then(([p, d, q]) => { setPerfis(p.data.perfis); setDocumentos(d.data.documentos); setQuizzes(q.data.quizzes); })
      .catch(() => {});
    carregar();
    window.addEventListener('focus', carregar);
    return () => window.removeEventListener('focus', carregar);
  }, []);

  useEffect(() => {
    if (!editando) return;
    api.get(`/trilhas/${id}`).then(({ data }) => {
      const t = data.trilha;
      setTitulo(t.titulo || ''); setDescricao(t.descricao || '');
      setObrigatoria(!!t.obrigatoria); setSequencial(!!t.sequencial); setPublicada(t.publicada !== false);
      setPerfisSel(data.perfis || []);
      setItens((data.itens || []).map(i => ({
        tipo: i.tipo, documento_id: i.documento_id, quiz_id: i.quiz_id, titulo: i.titulo,
      })));
    }).catch(() => { Swal.fire('Erro', 'Não foi possível carregar a trilha.', 'error'); navigate('/academia'); });
  }, [id, editando, navigate]);

  const addDoc = (docId) => {
    const d = documentos.find(x => x.id === parseInt(docId));
    if (d) setItens(it => [...it, { tipo: 'documento', documento_id: d.id, titulo: d.titulo }]);
  };
  const addQuiz = (qId) => {
    const q = quizzes.find(x => x.id === parseInt(qId));
    if (q) setItens(it => [...it, { tipo: 'quiz', quiz_id: q.id, titulo: q.titulo }]);
  };
  const removeItem = (i) => setItens(it => it.filter((_, j) => j !== i));
  const mover = (i, dir) => setItens(it => {
    const j = i + dir;
    if (j < 0 || j >= it.length) return it;
    const novo = [...it]; [novo[i], novo[j]] = [novo[j], novo[i]]; return novo;
  });

  async function salvar(e) {
    e.preventDefault();
    if (!titulo.trim()) return Swal.fire('Falta o título', 'Dê um nome à trilha.', 'warning');
    if (itens.length === 0) return Swal.fire('Trilha vazia', 'Adicione pelo menos uma aula ou teste.', 'warning');
    setSalvando(true);
    const payload = {
      titulo: titulo.trim(), descricao: descricao.trim() || null,
      obrigatoria, sequencial, publicada, perfis: perfisSel,
      itens: itens.map(i => ({ tipo: i.tipo, documento_id: i.documento_id, quiz_id: i.quiz_id })),
    };
    try {
      let tid = id;
      if (editando) await api.put(`/trilhas/${id}`, payload);
      else { const { data } = await api.post('/trilhas', payload); tid = data.id; }
      await Swal.fire({ icon: 'success', title: 'Trilha salva!', timer: 1300, showConfirmButton: false });
      navigate(`/academia/trilhas/${tid}`);
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  async function excluir() {
    const r = await Swal.fire({
      icon: 'warning', title: 'Excluir esta trilha?',
      text: 'Ela some da Academia pra todo mundo. O progresso de quem já fez fica guardado no histórico.',
      showCancelButton: true, confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#C62828',
    });
    if (!r.isConfirmed) return;
    try {
      await api.delete(`/trilhas/${id}`);
      await Swal.fire({ icon: 'success', title: 'Trilha excluída', timer: 1300, showConfirmButton: false });
      navigate('/academia');
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível excluir.', 'error');
    }
  }

  const perfisOptions = perfis.map(p => ({ value: p.id, label: p.titulo }));

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 820 }}>
        <Link to="/academia" className="doc-voltar"><i className="bi bi-arrow-left"></i> Academia</Link>
        <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 16 }}>{editando ? 'Editar trilha' : 'Nova trilha'}</h1>

        <form className="conteudo-card doc-form" onSubmit={salvar}>
          <label className="doc-label">Título *</label>
          <input className="doc-input" value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={200} autoFocus />

          <label className="doc-label">Descrição</label>
          <input className="doc-input" value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Sobre o que é a trilha" />

          <div className="doc-form-linha" style={{ marginTop: 12 }}>
            <label className="doc-check" style={{ flex: 1, marginTop: 0 }}>
              <input type="checkbox" checked={obrigatoria} onChange={e => setObrigatoria(e.target.checked)} />
              Obrigatória (cobra conclusão)
            </label>
            <label className="doc-check" style={{ flex: 1, marginTop: 0 }}>
              <input type="checkbox" checked={sequencial} onChange={e => setSequencial(e.target.checked)} />
              Sequencial (trava a próxima até concluir)
            </label>
          </div>
          <label className="doc-check">
            <input type="checkbox" checked={publicada} onChange={e => setPublicada(e.target.checked)} />
            Publicada (visível aos colaboradores) — desmarque pra montar a trilha antes de liberar
          </label>

          <label className="doc-label" style={{ marginTop: 12 }}>Atribuir aos cargos</label>
          <p className="doc-hint">Sem nenhum cargo: se for <strong>obrigatória</strong>, vale pra <strong>todos os colaboradores</strong>; se não, fica no catálogo livre.</p>
          <MultiSelectBusca options={perfisOptions} values={perfisSel} onChange={setPerfisSel} placeholder="Buscar cargo…" icon="bi-people" />

          <label className="doc-label" style={{ marginTop: 16 }}>Itens da trilha (em ordem)</label>
          <div className="trilha-edit-itens">
            {itens.map((it, i) => (
              <div className="trilha-edit-item" key={i}>
                <span className="trilha-edit-ordem">{i + 1}</span>
                <i className={`bi ${it.tipo === 'quiz' ? 'bi-patch-question-fill' : 'bi-file-earmark-text'}`}></i>
                <span className="trilha-edit-item-titulo">{it.titulo}</span>
                <span className="trilha-edit-item-tipo">{it.tipo === 'quiz' ? 'Teste' : 'Aula'}</span>
                <div className="trilha-edit-item-acoes">
                  <a href={it.tipo === 'quiz' ? `/admin/quizzes/${it.quiz_id}/editar` : `/admin/documentos/${it.documento_id}/editar`}
                     target="_blank" rel="noreferrer" title={it.tipo === 'quiz' ? 'Editar o quiz (nova aba)' : 'Editar a aula (nova aba)'}>
                    <i className="bi bi-pencil"></i>
                  </a>
                  <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} title="Subir"><i className="bi bi-arrow-up"></i></button>
                  <button type="button" onClick={() => mover(i, 1)} disabled={i === itens.length - 1} title="Descer"><i className="bi bi-arrow-down"></i></button>
                  <button type="button" onClick={() => removeItem(i)} title="Remover"><i className="bi bi-x-lg"></i></button>
                </div>
              </div>
            ))}
            {itens.length === 0 && <div className="trilha-edit-vazio">Nenhum item ainda. Adicione aulas e testes abaixo.</div>}
          </div>

          <div className="doc-form-linha" style={{ marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <select className="doc-input" value="" onChange={e => { if (e.target.value) addDoc(e.target.value); e.target.value = ''; }}>
                <option value="">+ Adicionar aula (documento)…</option>
                {documentos.map(d => <option key={d.id} value={d.id}>{d.titulo}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <select className="doc-input" value="" onChange={e => { if (e.target.value) addQuiz(e.target.value); e.target.value = ''; }}>
                <option value="">+ Adicionar teste (quiz)…</option>
                {quizzes.map(q => <option key={q.id} value={q.id}>{q.titulo}</option>)}
              </select>
            </div>
          </div>
          <a href="/admin/quizzes/nova" target="_blank" rel="noreferrer" className="trilha-edit-novoquiz"><i className="bi bi-plus-lg"></i> Criar um quiz novo (nova aba)</a>

          <div className="doc-form-acoes">
            <button type="submit" className="btn-centrao" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar trilha'}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/academia')}>Cancelar</button>
            {editando && (
              <button type="button" className="btn btn-outline-danger" style={{ marginLeft: 'auto' }} onClick={excluir}>
                <i className="bi bi-trash me-1"></i> Excluir trilha
              </button>
            )}
          </div>
        </form>
      </main>
    </>
  );
}
